import { useContext, useMemo } from 'react'
import NextLink from 'next/link'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { ManagerContext } from '@/contexts/ManagerContext'

export default function UsageWarning() {
  const managerContext = useContext(ManagerContext)

  const getSeverity = maxPercentage => {
    if (maxPercentage >= 95) return 'error'
    if (maxPercentage >= 85) return 'warning'
    return 'info'
  }

  const { maxPercentage, inputPercentage, outputPercentage } = useMemo(() => {
    if (!managerContext.currentUsage)
      return { maxPercentage: 0, inputPercentage: 0, outputPercentage: 0 }
    const { inputPercentage, outputPercentage } = managerContext.currentUsage
    return {
      maxPercentage: Math.max(inputPercentage, outputPercentage),
      inputPercentage,
      outputPercentage,
    }
  }, [managerContext.currentUsage])

  const { alertContent, expanded } = useMemo(() => {
    if (!managerContext.currentUsage) {
      return { expanded: false, alertContent: null }
    }

    if (managerContext.billingDetails?.tier === 'Free') {
      return {
        expanded: true,
        alertContent: (
          <Alert className="mt-2 border-amber-200 bg-amber-50 text-amber-900">
            <AlertDescription>
              You are currently not on a paid plan.{' '}
              <NextLink
                href="/manager/settings?tab=billing"
                className="text-foreground underline hover:no-underline"
              >
                Subscribe to unlock Tectra&apos;s full capabilities.
              </NextLink>
            </AlertDescription>
          </Alert>
        ),
      }
    }
    if (maxPercentage >= 100) {
      return {
        expanded: true,
        alertContent: (
          <Alert variant="destructive" className="mt-2">
            <AlertDescription>
              You&apos;ve used your full allowance of AI tokens.{' '}
              <NextLink
                href="/manager/settings?tab=billing"
                className="underline hover:no-underline"
              >
                Add more
              </NextLink>
            </AlertDescription>
          </Alert>
        ),
      }
    }

    const severity = getSeverity(maxPercentage)

    if (severity !== 'info') {
      const classes =
        severity === 'error'
          ? 'border-red-200 bg-red-50 text-red-900'
          : 'border-amber-200 bg-amber-50 text-amber-900'
      return {
        expanded: true,
        alertContent: (
          <Alert className={classes}>
            <AlertDescription>
              You&apos;ve almost used your full allowance of AI tokens.
              <ul className="ml-4 mt-1 list-disc">
                <li>
                  Input: <b>{inputPercentage}%</b>
                </li>
                <li>
                  Output: <b>{outputPercentage}%</b>
                </li>
              </ul>
            </AlertDescription>
          </Alert>
        ),
      }
    }

    return { alertContent: null, expanded: false }
  }, [managerContext.billingDetails?.tier, maxPercentage])

  if (!expanded) return <div className="usage-warning" />

  return <div className="usage-warning">{alertContent}</div>
}
